import React, { useCallback, useMemo, useState } from 'react';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import GravTitelKort from '../GravTitelKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { EDControlledExpansionPanel } from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import {
  arbUndStatusLabel,
  formatYearRange,
  getCurrentSkotselIds,
  hasHistoricalData,
} from '../Utils/skotselUtils';
import { shouldShowField } from '../Utils';
import { TEDGASkotselArbUndStatusEnum } from '../../../../../../globalQueryTypes';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface ISkotselProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

export const Skotsel: React.FC<ISkotselProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isChecked, setIsChecked] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const currentSkotsel = useMemo(() => {
    return nodeData?._Associations?.GiltigaSkotslar_All || [];
  }, [nodeData]);

  const allSkotselData = useMemo(() => {
    return nodeData?._Associations?.Skotslar_All || [];
  }, [nodeData]);

  const currentSkotselIds = useMemo(
    () => getCurrentSkotselIds(currentSkotsel),
    [currentSkotsel]
  );

  const displayedSkotsel = useMemo(() => {
    return isChecked
      ? allSkotselData
      : allSkotselData.filter(item => currentSkotselIds.has(item?.id));
  }, [isChecked, allSkotselData, currentSkotselIds]);

  const onChange = useCallback((itemId: string) => {
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  const handleShowDetailsChange = useCallback(
    (checked: boolean) => {
      setShowDetails(checked);
      if (checked) {
        // Expandera alla
        const allKeys = new Set(
          displayedSkotsel.map((item, index) => item?.id ?? `item-${index}`)
        );
        setExpanded(allKeys);
      } else {
        // Aollapsa alla
        setExpanded(new Set());
      }
    },
    [displayedSkotsel]
  );

  const hasHistoricalDataFlag = hasHistoricalData(
    currentSkotsel.length,
    allSkotselData.length
  );

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Skötsel'}
      collectionName={GravModel.CollectionName}
    >
      <ObjektVyKolumner>
        <ObjektVyKolumn fullPage={fullPage}>
          <GravTitelKort nodeData={nodeData} />
          <ObjektVyKort caption="Skötselsuppgifter">
            <ObjektVyKort noBoxShadow>
              {hasHistoricalDataFlag && (
                <EDCheckbox
                  label="Visa historisk skötsel"
                  checked={isChecked}
                  onCheck={setIsChecked}
                />
              )}
              <EDCheckbox
                label="Visa Detaljer"
                checked={showDetails}
                onCheck={handleShowDetailsChange}
              />
            </ObjektVyKort>
            {displayedSkotsel.length > 0 ? (
              displayedSkotsel.map((item, index) => {
                const itemKey = item?.id ?? `item-${index}`;
                const isExpanded = expanded.has(itemKey);
                const isHistorical = !currentSkotselIds.has(item?.id);

                return (
                  <ObjektVyKort
                    key={itemKey}
                    className="position-relative"
                    backgroundColor={'white'}
                    cardInCard
                    noPadding
                  >
                    <EDControlledExpansionPanel
                      expanded={isExpanded}
                      onChange={() => onChange(itemKey)}
                      overflowHidden
                      label={
                        <EDTypography
                          variant="h6"
                          color={isHistorical ? 'textSecondary' : 'black'}
                          width={'100%'}
                          p={2}
                          noWrap
                        >
                          {item?.BenamningStr || '-'}
                        </EDTypography>
                      }
                    >
                      {(item?.AR_FR || item?.AR_TI) && (
                        <EDCaptionedValue
                          label="År"
                          name={`ar_${index}`}
                          size="large"
                        >
                          <EDTypography>
                            {formatYearRange(item?.AR_FR, item?.AR_TI)}
                          </EDTypography>
                        </EDCaptionedValue>
                      )}

                      {item?._Associations?.Artikel?.ARTIKELNR && (
                        <EDCaptionedValue
                          label="Artikel"
                          name={`artikel_${index}`}
                          size="large"
                        >
                          <EDTypography>
                            {item._Associations.Artikel.ARTIKELNR}
                          </EDTypography>
                        </EDCaptionedValue>
                      )}

                      {item?.BENAMNING && (
                        <EDCaptionedValue
                          label="Benämning"
                          name={`benamning_${index}`}
                          size="large"
                        >
                          <EDTypography>{item.BENAMNING}</EDTypography>
                        </EDCaptionedValue>
                      )}

                      {shouldShowField(item?.ANTAL) && (
                        <EDCaptionedValue
                          label="Antal"
                          name={`antal_${index}`}
                          size="large"
                        >
                          <EDTypography>{item.ANTAL}</EDTypography>
                        </EDCaptionedValue>
                      )}

                      {shouldShowField(item?.MANAD) && (
                        <EDCaptionedValue
                          label="Månad"
                          name={`manad_${index}`}
                          size="large"
                        >
                          <EDTypography>{item.MANAD}</EDTypography>
                        </EDCaptionedValue>
                      )}

                      {shouldShowField(item?.DAG) && (
                        <EDCaptionedValue
                          label="Dag"
                          name={`dag_${index}`}
                          size="large"
                        >
                          <EDTypography>{item.DAG}</EDTypography>
                        </EDCaptionedValue>
                      )}
                      {shouldShowField(item?.ArbUndStatus) && (
                        <EDCaptionedValue
                          label="Arbetsunderlag"
                          name={`arbetsunderlag_${index}`}
                          size="large"
                        >
                          <EDTypography>
                            {arbUndStatusLabel[
                              item.ArbUndStatus as TEDGASkotselArbUndStatusEnum
                            ] ?? item.ArbUndStatus}
                          </EDTypography>
                        </EDCaptionedValue>
                      )}
                    </EDControlledExpansionPanel>
                  </ObjektVyKort>
                );
              })
            ) : (
              <ObjektVyKort
                className="position-relative"
                backgroundColor={'white'}
              >
                <EDTypography>Ingen aktiv skötsel registrerad</EDTypography>
              </ObjektVyKort>
            )}
          </ObjektVyKort>
        </ObjektVyKolumn>
      </ObjektVyKolumner>
    </ObjektVySida>
  );
};

export default Skotsel;
